import { useEffect, useState } from "react";
import api from "@/lib/api";
import { X, Mail, Loader2, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const EmailReportDialog = ({ inspectionId, open, onClose, defaultName = "" }) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState(defaultName);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  const [photoReport, setPhotoReport] = useState(null);
  const [attachPr, setAttachPr] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get(`/inspections/${inspectionId}/photo-report`).then((r) => {
      const vs = r.data?.versions || [];
      setPhotoReport(vs.length ? vs[vs.length - 1] : null);
    }).catch(() => setPhotoReport(null));
  }, [open, inspectionId]);

  if (!open) return null;

  const close = () => {
    if (sending) return;
    setEmail("");
    setName(defaultName);
    setMessage("");
    setSent(null);
    onClose();
  };

  const send = async () => {
    if (!email) {
      toast.error("Enter the recipient's email");
      return;
    }
    setSending(true);
    try {
      const r = await api.post(`/inspections/${inspectionId}/email`, {
        recipient_email: email,
        recipient_name: name,
        message,
        attach_photo_report: attachPr,
      });
      setSent(r.data);
      toast.success("Report sent");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send email");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-150"
      onClick={close}
      data-testid="email-dialog-overlay"
    >
      <div
        className="bg-white border-2 border-slate-900 w-full max-w-lg shadow-[8px_8px_0px_0px_rgba(15,23,42,0.2)]"
        onClick={(e) => e.stopPropagation()}
        data-testid="email-dialog"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b-2 border-slate-200">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-[#1E1B4B] text-white flex items-center justify-center">
              <Mail size={18} strokeWidth={2.5} />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Send report</p>
              <h3 className="font-black text-slate-900 leading-tight">Email to the owner</h3>
            </div>
          </div>
          <button onClick={close} data-testid="email-dialog-close" disabled={sending} className="w-9 h-9 border-2 border-slate-300 flex items-center justify-center hover:bg-slate-100 disabled:opacity-40">
            <X size={18} strokeWidth={2.5} />
          </button>
        </div>

        {sent ? (
          <div className="p-6 text-center" data-testid="email-success">
            <div className="w-14 h-14 mx-auto bg-emerald-100 border-2 border-emerald-300 flex items-center justify-center mb-3">
              <CheckCircle2 size={28} className="text-emerald-700" strokeWidth={2.5} />
            </div>
            <h4 className="font-black text-slate-900 text-lg">Sent to {sent.to}</h4>
            <p className="text-sm text-slate-600 mt-1">The PDF report has been delivered.</p>
            <button onClick={close} data-testid="email-success-close" className="mt-5 h-11 px-5 bg-slate-900 text-white border-2 border-slate-900 font-bold text-sm hover:bg-slate-800">
              Done
            </button>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            <label className="block">
              <span className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">Recipient email *</span>
              <input
                data-testid="email-recipient-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="owner@example.com"
                className="h-12 w-full px-3 border-2 border-slate-300 bg-white focus:border-[#1E1B4B] focus:outline-none text-base"
              />
            </label>
            <label className="block">
              <span className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">Recipient name</span>
              <input
                data-testid="email-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Optional, e.g. Jane Smith"
                className="h-12 w-full px-3 border-2 border-slate-300 bg-white focus:border-[#1E1B4B] focus:outline-none text-base"
              />
            </label>
            <label className="block">
              <span className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">Personal note (optional)</span>
              <textarea
                data-testid="email-message-input"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                placeholder="A short message to the owner — e.g. happy to chat about the fixes."
                className="w-full p-3 border-2 border-slate-300 bg-white focus:border-[#1E1B4B] focus:outline-none text-base"
              />
            </label>
            <p className="text-xs text-slate-500 leading-relaxed bg-slate-50 border border-slate-200 p-3">
              The PDF report (checklist, photos &amp; AI compliance summary) will be attached automatically.
            </p>
            {photoReport && (
              <label className="flex items-center gap-2.5 border-2 border-teal-200 bg-teal-50 p-3 cursor-pointer" data-testid="email-attach-photo-report">
                <input type="checkbox" checked={attachPr} onChange={(e) => setAttachPr(e.target.checked)}
                  className="h-5 w-5 accent-[#00F2C3]" data-testid="email-attach-photo-report-checkbox"/>
                <span className="text-sm text-slate-800">
                  <strong>Also attach Photo Report</strong> (v{photoReport.version}, {(photoReport.generated_at || "").slice(0, 10)}) as a second PDF
                </span>
              </label>
            )}
            <div className="flex gap-2 pt-2">
              <button
                onClick={send}
                disabled={sending}
                data-testid="email-send-button"
                className="flex-1 h-12 inline-flex items-center justify-center gap-2 bg-[#1E1B4B] text-white font-bold border-2 border-[#1E1B4B] hover:bg-[#0F0E2E] disabled:opacity-60"
              >
                {sending ? <><Loader2 size={16} className="animate-spin"/>Sending…</> : <><Send size={16} strokeWidth={2.5}/>Send report</>}
              </button>
              <button
                onClick={close}
                disabled={sending}
                data-testid="email-cancel-button"
                className="h-12 px-5 bg-white border-2 border-slate-900 text-slate-900 font-bold hover:bg-slate-100 disabled:opacity-60"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailReportDialog;
